import { useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePrecosDosPlanos } from '@/hooks/useCobrancas';
import type { PrecoPlano } from '@/api/endpoints/cobrancas';
import { centavosDeTexto, reaisDeCentavos } from './formato';
import { simularParcelas, type OpcaoParcelamento, type ParteDoCliente } from './simuladorParcelas';

const inputCls =
  'w-full rounded-lg border border-border bg-bg-main px-3 py-2.5 text-sm text-text-title outline-none transition-colors placeholder:text-text-hint focus:border-primary';

const QUEM_PAGA: { valor: ParteDoCliente; rotulo: string; apoio: string }[] = [
  { valor: 1, rotulo: 'Cliente', apoio: 'Você recebe o valor cheio' },
  { valor: 0.5, rotulo: 'Dividir', apoio: 'Metade do acréscimo para cada' },
  { valor: 0, rotulo: 'Curral Tech', apoio: 'Cliente paga o preço' },
];

const chavePreco = (p: Pick<PrecoPlano, 'planoCodigo' | 'ciclo'>) => `${p.planoCodigo}:${p.ciclo}`;

// Texto para colar no WhatsApp do cliente, sem emoji e sem travessão.
function textoDaSimulacao(descricao: string, opcoes: OpcaoParcelamento[]): string {
  const linhas = opcoes.map((o) =>
    o.parcelas === 1
      ? `À vista no cartão: ${reaisDeCentavos(o.totalCentavos)}`
      : `${o.parcelas}x de ${reaisDeCentavos(o.parcelaCentavos)} (total ${reaisDeCentavos(o.totalCentavos)})`
  );
  return [`Simulação no cartão de crédito, ${descricao}:`, '', ...linhas].join('\n');
}

interface SimuladorParcelasModalProps {
  aberto: boolean;
  onClose: () => void;
}

/**
 * Quanto o cliente paga e quanto a Curral Tech recebe em cada opção de
 * parcelas no cartão, conforme quem fica com a taxa da AbacatePay. O link de
 * pagamento cobra com a taxa do cliente; as outras opções servem para negociar.
 */
export function SimuladorParcelasModal({ aberto, onClose }: SimuladorParcelasModalProps) {
  const { data: precos = [] } = usePrecosDosPlanos();
  const [planoEscolhido, setPlanoEscolhido] = useState<string | null>(null);
  const [valorTexto, setValorTexto] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [parteDoCliente, setParteDoCliente] = useState<ParteDoCliente>(1);

  const preco = precos.find((p) => chavePreco(p) === planoEscolhido) ?? null;
  const valorDigitado = centavosDeTexto(valorTexto);
  const valor = valorDigitado ?? preco?.valorCentavos ?? null;
  const opcoes = useMemo(
    () => (valor ? simularParcelas(valor, parteDoCliente) : []),
    [valor, parteDoCliente]
  );

  const descricao =
    preco && valorDigitado == null
      ? `plano ${preco.planoNome} ${preco.ciclo === 'anual' ? 'anual' : 'mensal'} (${reaisDeCentavos(preco.valorCentavos)})`
      : `valor de ${reaisDeCentavos(valor ?? 0)}`;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(textoDaSimulacao(descricao, opcoes));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  };

  const fechar = () => {
    setPlanoEscolhido(null);
    setValorTexto('');
    setCopiado(false);
    setParteDoCliente(1);
    onClose();
  };

  return (
    <Modal isOpen={aberto} onClose={fechar} title="Simular parcelas no cartão" size="md">
      <div className="flex flex-col gap-5 p-6 max-md:p-4">
        <fieldset>
          <legend className="mb-2 text-[13px] font-medium text-text-secondary">Plano</legend>
          <div className="grid grid-cols-2 gap-2">
            {precos.map((p) => {
              const ativo = planoEscolhido === chavePreco(p) && valorDigitado == null;
              return (
                <button
                  key={chavePreco(p)}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => {
                    setPlanoEscolhido(chavePreco(p));
                    setValorTexto('');
                  }}
                  className={cn(
                    'flex flex-col items-start rounded-xl border px-3 py-2.5 text-left transition-colors',
                    ativo ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                  )}
                >
                  <span className="text-xs text-text-secondary">
                    {p.planoNome} · {p.ciclo === 'anual' ? 'anual' : 'mensal'}
                  </span>
                  <span className="text-base font-bold tabular-nums text-text-title">
                    {reaisDeCentavos(p.valorCentavos)}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-text-secondary">
            Ou outro valor (R$)
          </span>
          <input
            className={inputCls}
            inputMode="decimal"
            placeholder="Ex.: 350,00"
            value={valorTexto}
            onChange={(e) => setValorTexto(e.target.value)}
          />
        </label>

        <fieldset>
          <legend className="mb-2 text-[13px] font-medium text-text-secondary">
            Quem paga a taxa do cartão
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {QUEM_PAGA.map((q) => {
              const ativo = parteDoCliente === q.valor;
              return (
                <button
                  key={q.valor}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => setParteDoCliente(q.valor)}
                  className={cn(
                    'flex flex-col items-center gap-0.5 rounded-xl border px-2 py-2.5 text-center transition-colors',
                    ativo
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-text-secondary hover:bg-muted'
                  )}
                >
                  <span className="text-sm font-semibold">{q.rotulo}</span>
                  <span className="text-[11px] leading-tight text-text-hint max-sm:hidden">
                    {q.apoio}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {opcoes.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-text-secondary">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Parcelas</th>
                  <th className="px-3 py-2 text-right font-semibold">Parcela</th>
                  <th className="px-3 py-2 text-right font-semibold">Cliente paga</th>
                  <th className="px-3 py-2 text-right font-semibold max-sm:hidden">Taxa</th>
                  <th className="px-3 py-2 text-right font-semibold">Você recebe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border tabular-nums">
                {opcoes.map((o) => (
                  <tr key={o.parcelas}>
                    <td className="px-3 py-2 font-medium text-text-title">
                      {o.parcelas === 1 ? 'À vista' : `${o.parcelas}x`}
                    </td>
                    <td className="px-3 py-2 text-right text-text-title">
                      {reaisDeCentavos(o.parcelaCentavos)}
                    </td>
                    <td className="px-3 py-2 text-right text-text-body">
                      {reaisDeCentavos(o.totalCentavos)}
                    </td>
                    <td className="px-3 py-2 text-right text-text-secondary max-sm:hidden">
                      {reaisDeCentavos(o.taxaCentavos)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-text-title">
                      {reaisDeCentavos(o.recebidoCentavos)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-text-secondary">
            Escolha um plano ou digite um valor para ver as parcelas.
          </p>
        )}

        <p className="text-xs text-text-hint">
          O link de pagamento cobra com a taxa do cliente. Parcela mínima de R$ 10. Pix e boleto não
          têm acréscimo. Taxas da tabela pública da AbacatePay.
        </p>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={fechar}>
            Fechar
          </Button>
          <Button type="button" onClick={copiar} disabled={opcoes.length === 0}>
            {copiado ? <Check size={16} /> : <Copy size={16} />}
            {copiado ? 'Copiado' : 'Copiar para o cliente'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
